import React, { useState } from "react";
import axios from "axios";
import { Star, Send, ExternalLink, Check } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "../lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const REVIEW_SUBMITTED_EVENT = "global-pneus:review-submitted";

export const ReviewBox = () => {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [loading, setLoading] = useState(false);
  const [showGoogleModal, setShowGoogleModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const cleanName = name.trim();
    const cleanText = text.trim();
    if (!cleanName || cleanName.length < 2) {
      toast.error("Indique o seu nome.");
      return;
    }
    if (cleanText.length < 5) {
      toast.error("Escreva um comentário com pelo menos 5 caracteres.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        nome: cleanName,
        rating,
        texto: cleanText,
      });
      toast.success("Obrigado!", {
        description: "A sua opinião foi publicada e já aparece nas críticas.",
      });
      setName("");
      setText("");
      setRating(5);
      window.dispatchEvent(new Event(REVIEW_SUBMITTED_EVENT));
      // 4★+ reviews are prompted to also publish on Google — huge SEO win.
      if (rating >= 4) {
        setShowGoogleModal(true);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Não foi possível publicar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="review-section"
      id="opiniao"
      className="relative py-10 md:py-12 bg-[#0E2452] border-t border-[#2F568F]"
    >
      <div className="w-full px-6 md:px-12 lg:px-20">
        <div
          data-testid="review-box"
          className="bg-[#163A6D] border border-[#2F568F] p-5 max-w-5xl mx-auto rounded-2xl"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
            <div>
              <span className="section-label">// Deixe a sua opinião</span>
              <h3 className="font-display font-semibold uppercase text-white text-lg md:text-xl mt-1">
                Como foi a sua experiência?
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`review-star-${n}`}
                  onClick={() => setRating(n)}
                  aria-label={`${n} estrelas`}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={22}
                    className={
                      n <= rating
                        ? "fill-[#FBBC05] text-[#FBBC05]"
                        : "text-[#5A7AAE] hover:text-[#FBBC05]"
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              data-testid="review-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="O seu nome"
              maxLength={80}
              className="w-full bg-[#0E2452] border border-[#2F568F] text-white placeholder:text-[#5A7AAE] px-4 h-11 focus:border-[#FF5A00] focus:outline-none focus:ring-1 focus:ring-[#FF5A00] transition-colors font-body text-sm"
              required
            />
            <div className="flex flex-col md:flex-row gap-3">
              <textarea
                data-testid="review-textarea"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Conte-nos como foi o atendimento, o serviço, o preço..."
                rows={3}
                maxLength={1000}
                className="flex-1 bg-[#0E2452] border border-[#2F568F] text-white placeholder:text-[#5A7AAE] px-4 py-3 focus:border-[#FF5A00] focus:outline-none focus:ring-1 focus:ring-[#FF5A00] transition-colors font-body text-sm resize-none"
                required
              />
              <button
                data-testid="review-submit-btn"
                type="submit"
                disabled={loading}
                className="btn-primary text-sm py-3 px-6 self-start md:self-auto whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {loading ? "A publicar..." : "Publicar opinião"}
              </button>
            </div>
          </form>

          <p className="text-[10px] text-[#5A7AAE] font-body mt-3 leading-relaxed">
            A sua opinião aparecerá imediatamente nas críticas do site.
          </p>

          {/* Google review CTA — always visible, secondary path */}
          <div className="mt-5 pt-5 border-t border-[#2F568F]/60 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <span className="section-label !text-[#FBBC05]">// Prefere publicar no Google?</span>
              <p className="text-xs text-white/70 font-body mt-1">
                Ajuda-nos a chegar a mais pessoas na sua zona.
              </p>
            </div>
            <a
              data-testid="google-review-cta"
              href={COMPANY.googleReviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-[#FBBC05] text-[#FBBC05] hover:bg-[#FBBC05] hover:text-black font-display font-bold uppercase tracking-widest text-xs transition-colors whitespace-nowrap"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Publicar no Google
            </a>
          </div>
        </div>
      </div>

      {/* Post-submit modal (only shown after 4★+ site reviews) */}
      {showGoogleModal && (
        <div
          data-testid="google-review-modal"
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowGoogleModal(false)}
        >
          <div
            className="max-w-md w-full bg-[#0E2452] border border-[#2F568F] p-6 md:p-8 text-center"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: "quizFadeIn 300ms ease-out" }}
          >
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FBBC05]/15 border border-[#FBBC05] flex items-center justify-center">
              <Check className="w-7 h-7 text-[#FBBC05]" />
            </div>
            <h4 className="font-display font-bold uppercase text-white text-lg md:text-xl mb-2">
              Obrigado pela sua opinião!
            </h4>
            <p className="text-sm text-white/80 leading-relaxed mb-6">
              Se puder, ajude-nos também no <strong>Google</strong> — é o local
              onde mais clientes nos encontram. Basta um clique.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <a
                data-testid="google-review-modal-cta"
                href={COMPANY.googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowGoogleModal(false)}
                className="btn-primary flex-1 text-sm py-3 !bg-[#FBBC05] !text-black hover:!bg-[#F5B302]"
                style={{ boxShadow: "0 0 24px rgba(251, 188, 5, 0.4)" }}
              >
                <ExternalLink className="w-4 h-4" />
                Deixar também no Google
              </a>
              <button
                data-testid="google-review-modal-dismiss"
                type="button"
                onClick={() => setShowGoogleModal(false)}
                className="px-4 py-3 border border-[#2F568F] text-white/80 hover:border-[#FF5A00] hover:text-[#FF5A00] font-display uppercase tracking-widest text-xs transition-colors"
              >
                Agora não
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ReviewBox;
